import { ApiCallResponse, ApiCallArgs, OfflineError } from "../app/api";
import { BaseApp } from "../app";
import { Attribute, AttributeKey } from "../db/models/attributes";
import { SyncMessage } from "../db/models/synchronizer";
import SyncCommand, { SyncCommandLike, SyncCommandData } from "../db/models/syncCommand";
import { DexieTable } from "../db/index";
import { EventSource } from "../event-source/index";
import { fileSyncPush, fileSyncPull, isBBSyncEnabled, cleanupPullMessages, getBBSyncStatusMessages, SyncStatusMessageLevel, SyncStatusMessage } from "../helpers/offline-sync";


export default class Synchronizer extends EventSource
{
    private app: BaseApp;
    private syncTables: Dexie.Table<any, any>[];
    private currentlySynchronizing: boolean;
    private isNextPullScheduled = false;
    private _lastAutoSyncResult: AutoSyncResult = AutoSyncResult.NO_CHANGES;
    get lastAutoSyncResult() {
        return this._lastAutoSyncResult;
    }
    private _lastSyncTimestamp: Attribute;
    private didChangeLastSyncTimestamp = false;

    // Timeout handle for auto synchronization
    private syncTimeout = null;

    public get lastSyncTimestamp() {return this._lastSyncTimestamp; }

    constructor(app: BaseApp)
    {
        super();
        this.app = app;
        this.currentlySynchronizing = false;
    }

    public init()
    {
        this.syncTables = [];
        
        for(let modelName of this.app.settings.clientSync.models)
        {
            this.syncTables.push(this.app.db[modelName]);
        }

        this.pollSyncStatusMessages();
        this.tryDisplayNextSyncStatusMessage();
    }

    syncStatusMessages: {
        queue: SyncStatusMessage[],
        pollInterval: number,
    } = {
        queue: [],
        pollInterval: 1000,
    }

    public async pollSyncStatusMessages() {
        try {
            const messages = await getBBSyncStatusMessages();
            for (const message of messages) {
                this.syncStatusMessages.queue.push(message);
                console.log(message);
            }
        } catch (error) {
            console.error(error);
            console.warn(`Failed to load sync status messages. Trying again in ${this.syncStatusMessages.pollInterval} ms`);
        }

        window.setTimeout(() => this.pollSyncStatusMessages(), this.syncStatusMessages.pollInterval);
    }

    public async tryDisplayNextSyncStatusMessage() {
        if (this.syncStatusMessages.queue.length == 0) {
            // Queue next display update
            window.setTimeout(() => this.tryDisplayNextSyncStatusMessage(), this.syncStatusMessages.pollInterval);
        } else {
            const message = this.syncStatusMessages.queue.shift();
            console.log('Displaying message', message);
            let promise: PromiseLike<any>;
            switch (message.level) {
                case SyncStatusMessageLevel.Information:
                    promise = this.app.toasts.show(message.message, 10000, 'im im-info', true);
                    break;
                case SyncStatusMessageLevel.Error:
                    promise = this.app.toasts.show(message.message, 10000, 'im im-notification', true, true, 'is-danger');
                    break;
                default:
                    console.error(`Unsupported sync status message level "${message.level}"`);
                    break;
            }
            await promise;
            // Display next message immediately after the previous closes
            this.tryDisplayNextSyncStatusMessage();
        }
    }

    private handleApiCallError(error)
    {
        if (error instanceof OfflineError)
        {
            console.log('[ClientSync] Failed due to OfflineError.');
        }
    }

    private getLastSync(): PromiseLike<Attribute>
    {
        return this.app.db.Attribute.get(AttributeKey.LAST_SYNC);
    }

    private setLastSync(timestamp: Date): PromiseLike<any>
    {
        return this.app.db.Attribute.put({key: AttributeKey.LAST_SYNC, value: timestamp});
    }

    public pullUpdates(queueNext = false): PromiseLike<void>
    {
        console.log('pullUpdates');
        if (this.currentlySynchronizing)
        {
            return Promise.reject(new Error('Synchronization process is already running! Please try again later.'));
        }

        if (!this.app.isAuthenticated)
        {
            return Promise.reject(new Error('[ClientSync] Not authenticated, not pulling updates.'));
        }

        this.currentlySynchronizing = true;
        this._lastAutoSyncResult = AutoSyncResult.NO_CHANGES;
        let syncTime: Date;
        let apiError = null;

        return isBBSyncEnabled().then((enabled) => 
        {
            if (!navigator.onLine && !enabled)
            {
                return Promise.reject(new OfflineError('[ClientSync]: Offline, unable to sync.'));
            }

            return this.getLastSync().then((lastSync) =>
            {
                this._lastSyncTimestamp = lastSync;
    
                let args: {[key: string]: Object} = {version: this.app.settings.version.number};
                if (lastSync != null)
                {   
                    args.modified_at = lastSync.value;
                }
                
                if (enabled) {
                    console.log('sync: Calling fileSyncPull');
                    return fileSyncPull(this.app.apiToken, this.app.db);
                } else {
                    console.log('sync: Calling api-based sync (online, default)');
                    return this.app.api.private(this.app.settings.clientSync.apiEndpoints.pull, args);
                }
            }).then((response: ApiCallResponse): PromiseLike<any> =>
            {   
                if (Array.isArray(response)) {
                    let promises: PromiseLike<void>[] = [];
                    let syncTimes = [];
                    for(let resp of response)
                    {
                        syncTimes.push(resp.timestamp);
                        promises.push(this.app.db.transaction('rw', this.syncTables, () => this.processPullResponse(resp)));
                    }
                    // Get latest syncTime and do all promises
                    syncTime = syncTimes.sort((a, b) => a - b)[syncTimes.length - 1];
                    return Promise.all(promises);
                } else {
                    syncTime = new Date(response.timestamp);
                    return this.app.db.transaction('rw', this.syncTables, () => this.processPullResponse(response));
                }
            }).then(() =>
            {
                if (syncTime == null) {
                    console.info(`Sync timestamp is not set, not updating last sync timestamp.`);
                    this.didChangeLastSyncTimestamp = false;
                    return Promise.resolve();
                } else {
                    console.info(`Using sync timestamp`, syncTime);
                    this.didChangeLastSyncTimestamp = true;
                    return this.setLastSync(syncTime); 
                }
            }).then(() =>
            {
                this.currentlySynchronizing = false;
                this.afterPull(queueNext);
                cleanupPullMessages(this.app.apiToken).then(() => 
                {
                    return this.processCommands();
                }).then(() => {
                    return this.deleteOfflineRecords();
                }).then(() => {
                    this.trigger('synchronized');
                });
            }, (error) =>
            {
                apiError = error;
                this.handleApiCallError(error)
                this.currentlySynchronizing = false;
                return Promise.reject(error);
            });
        });
    }

    private afterPull(queueNext: boolean)
    {
        if (queueNext)
        {
            let autoSyncIntervalInSeconds = this.app.settings.clientSync.autoSyncInterval/1000;
            let nextSync = new Date(new Date().getTime() + this.app.settings.clientSync.autoSyncInterval).toLocaleString();

            
            if (this.syncTimeout) {
                clearTimeout(this.syncTimeout);
                console.info(`[ClientSync]: Sync reset - Next sync in ${autoSyncIntervalInSeconds} seconds (🕐 ${nextSync}).`);
            } else {
                console.info(`[ClientSync]: Next sync in ${autoSyncIntervalInSeconds} seconds (🕐 ${nextSync}).`);
            }

            this.trigger('autoSyncScheduled');
            this.syncTimeout = setTimeout(() =>
            {
                this.syncTimeout = null;
                this.trigger('autoSyncBegin');
                this.update(true).then(() =>
                {
                    this.trigger('autoSyncEnd', {result: this._lastAutoSyncResult});
                }, error => {
                    this.trigger('autoSyncEnd', {result: this._lastAutoSyncResult});
                });
            }, this.app.settings.clientSync.autoSyncInterval);
        }
    }

    public push(message: SyncMessage, update = true, refreshView = true): PromiseLike<void>
    {
        if (message.timestamp == null)
        {
            message.timestamp = new Date();
        }

        if (message.user_id == null)
        {
            message.user_id = this.app.user.id;
        }
        
        if (this.app.barcodeAuthentication.isEnabled && this.app.barcodeAuthentication.user && this.app.barcodeAuthentication.user.user_id) {
            message.user_id = this.app.barcodeAuthentication.user.user_id;
        }

        return this.app.db.SyncMessage.add(message).then((id) =>
        {
            this.trigger(SynchronizerEvent.SYNC_QUEUE_PUSHED);

            return this.pushMessages();
        }).then(() =>
        {
            if (update)
            {
                return this.pullUpdates().then(() =>
                {
                    if (refreshView)
                    {
                        return this.app.refreshCurrentView();
                    }
                }, error => null);
            }
            else
            {
                return Promise.resolve();
            }
        }, error => null);
    }
    
    public pushMessages(): PromiseLike<void>
    {
        console.log('pushMessages');
        return isBBSyncEnabled().then((enabled) =>
        {
            if (!navigator.onLine && !enabled) 
            {
                return Promise.resolve();
            }

            let processedMessages: SyncMessage[];
            return this.app.db.SyncMessage.orderBy('timestamp').toArray().then(messages => 
            {
                // Do not send an empty push
                if (messages.length == 0) {
                    return;
                }

                processedMessages = messages;
                if (enabled) {
                    return fileSyncPush(this.app.apiToken, messages);
                } else {
                    return this.app.api.private(this.app.settings.clientSync.apiEndpoints.push, {messages: messages});
                }
            },).then((response) =>
            {
                if (!processedMessages) {
                    this.trigger(SynchronizerEvent.SYNC_QUEUE_FLUSHED);
                    return;
                }

                let keys = processedMessages.map(item => item.id);
                return this.app.db.SyncMessage.bulkDelete(keys).then(() =>
                {
                    this.trigger(SynchronizerEvent.SYNC_QUEUE_FLUSHED);
                });
            }, (error) => {
                this.handleApiCallError(error);
                return Promise.reject(error);
            });
        });
    }
    
    /**
     * Update synchronizer: Push messages and then pull updates.
     */
    public update(queueNext = false)
    {
        return this.pushMessages().then(() =>
        {
            return this.pullUpdates(queueNext);
        }).then(() => null, error => {
            if (queueNext) {
                this.afterPull(queueNext);
            }
            return Promise.reject(error);
        });
    }

    public createMessageAndPull(message: SyncMessage, refreshView = true): PromiseLike<void>
    {
        return this.push(message, false).then(() =>
        {
            return this.pullUpdates();
        }, error => null).then(() =>
        {
            if (refreshView)
            {
                return this.app.refreshCurrentView();
            }
        }, error => null);
    }
    
    private processCommands(): PromiseLike<void>
    {
        return this.app.db.SyncCommand.toArray().then((commands) =>
        {
            let promises: PromiseLike<void>[] = [];
            
            for(let cmd of commands)
            {
                switch(cmd.action)
                {
                    case SyncCommand.ACTION_REMOVE_INSTANCE:
                        let data: SyncCommandData = JSON.parse(cmd.data);
                        let table = this.app.db[data.name] as DexieTable;
                        if (table != null)
                        {
                            promises.push(table.delete(data.key));
                        }
                        break;
                    case SyncCommand.ACTION_UPDATE_CONSTANCE:
                        promises.push(this.requestConstance());
                        break;
                    case SyncCommand.ACTION_FULL_RESET:
                        if (this._lastSyncTimestamp != null)
                        {
                            indexedDB.deleteDatabase(this.app.db.name);
                            location.reload();
                        }
  
                        break;
                    case SyncCommand.ACTION_APP_UPDATE:
                        if (this._lastSyncTimestamp != null)
                        {
                            this.app.promptDownloadUpdate();
                        }
                        break;
                    default:
                        console.error(`Unsupported sync command action "${cmd.action}", data:`, cmd.data);
                }
            }
            
            return Promise.all(promises).then(() =>
            {
                return this.app.db.SyncCommand.bulkDelete(commands.map(o => o.id));
            });
        });
    }

    private preprocessRow(obj: any)
    {
        const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;
        for (let key in obj)
        {
            let value = obj[key];
            if (dateFormat.test(value))
            {
                obj[key] = new Date(value);
            }
        }

        return obj;
    }

    private processPullResponse(response: ApiCallResponse)
    {
        let stats = {count: 0, objects: []};
        try
        {
            for (let table of this.syncTables)
            {
                let objects = response[table.name];
                
                if (Array.isArray(objects))
                {
                    for (let obj of objects)
                    {
                        obj = this.preprocessRow(obj);
                        // If the table supports is_offline field, set it to false in case we write on top an offline record
                        if (table.schema.indexes.some(i => i.name === 'is_offline')) {
                            obj.is_offline = 0;
                        }
                        table.put(obj);
                        stats.objects.push(obj);
                        stats.count++; 
                    }                   
                }
            }

            console.info(`[ClientSync]: Updated ${stats.count} objects.`, stats.objects);
            
            if (stats.count === 0)
                this._lastAutoSyncResult = AutoSyncResult.NO_CHANGES;
            else
                this._lastAutoSyncResult = AutoSyncResult.CHANGED;
        }
        catch (error)
        {
            throw error;
        }
    }

    private deleteOfflineRecords(): Promise<any> {
        // If nothing has changed, we can't delete offline records
        if (!this.didChangeLastSyncTimestamp && this.lastAutoSyncResult === AutoSyncResult.NO_CHANGES) {
            return Promise.resolve();
        }
        const promises: Dexie.Promise<any>[] = [];
        for (const table of this.app.db.tables) {
            if (!table.schema.indexes.some(i => i.name === 'is_offline')) {
                continue;
            }
            // For some reason boolean is not listed as an indexed type
            promises.push(table.where('is_offline').equals(1).delete().then((count) => {
                if (count > 0)
                    console.log(`Deleted ${count} offline instances of ${table.name}`);
            }));
            
        }
        return Promise.all(promises);
    }

    public requestConstance(): PromiseLike<void>
    {
        return this.app.api.private(this.app.settings.clientSync.apiEndpoints.constance, {}).then(constance =>
        {
            this.app.constance = constance;

            return this.app.db.Attribute.put({
                key: AttributeKey.CONSTANCE,
                value: constance
            });
        }, (error) => this.handleApiCallError(error)).then((key) =>
        {
            //flatten
        });
    }

    public hasMessagesInQueue(): PromiseLike<boolean>
    {
        return this.app.db.SyncMessage.count().then((count) =>
        {
            return count > 0;
        });
    }
}

export enum AutoSyncResult
{
    NO_CHANGES = 'no_changes',
    CHANGED = 'modified'
}

export interface AutoSyncEndEvent
{
    result: AutoSyncResult;
}

export enum SynchronizerEvent
{
    SYNC_QUEUE_PUSHED = 'sync_queue_pushed',
    SYNC_QUEUE_FLUSHED = 'sync_queue_flushed'
}